import { Component } from "react";

import appsignal from "../../config/appsignal";
import { ALERT_TYPE_ERROR, ALERT_TYPE_INFO } from "../Alerts/Alert";
import { isPageNotFound } from "../Utils/errorsHandling";

// This is the base class to handle errors in the application. It provides a common
// behavior that's useful for all other boundaries.
//
// Note that the only issue with this pattern is that we need to wrap all the final
// classes with the `withAlert` HOC, which is not ideal. We don't seem to have better
// choices to share the alert behavior as of now, but seems an okay.
class BaseErrorBoundary extends Component {
  state = { hasError: false, error: null, errorData: null };

  constructor(props) {
    super(props);

    if (!this.props.showAlert && !this.showAlert) {
      throw new Error(
        `All error boundary classes inheriting from BaseErrorBoundary component must be
        wrapped in the withAlert HOC to allow the object to show alerts.`
      );
    }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, errorData: error.response };
  }

  // The notice is shown from both lifecycles, as an error thrown while the children
  // first render commits the boundary directly in error state.
  componentDidMount() {
    this.showNoticeAlert();
  }

  componentDidCatch(error, errorInfo) {
    // Boundaries also catch expected client-side conditions (any 4xx not-found / access
    // denied, or a "needs to sign in" response) that carry an HTTP `response` and are
    // rendered as a fallback page or alert — nominal navigation, not bugs. We forward
    // everything else (5xx failures and responseless render crashes) to AppSignal, since
    // those are unexpected exceptions swallowed here to keep the user flow alive. The React
    // component stack is attached so the crashing subtree is visible in the report.
    if (isPageNotFound(error) || error?.response?.needsToSignIn) return;

    appsignal.sendError(error, span => {
      if (errorInfo?.componentStack) {
        span.setTags({ react_component_stack: errorInfo.componentStack });
      }
    });
  }

  componentDidUpdate(_prevProps, prevState) {
    this.showNoticeAlert(prevState.errorData?.notice);
  }

  showNoticeAlert(previousNotice) {
    const newNotice = this.state.errorData?.notice;

    if (newNotice && newNotice !== previousNotice) {
      // If the error message is about the user needing to sign in, we show an info alert
      // instead of an error alert, as it's not meant to be understood as an error by the
      // viewer.
      const type = this.needsToSignIn ? ALERT_TYPE_INFO : ALERT_TYPE_ERROR;
      const showAlert = this.props.showAlert || this.showAlert;

      showAlert({ type, message: newNotice });
    }
  }

  get needsToSignIn() {
    return this.state.errorData?.needsToSignIn || false;
  }

  get notice() {
    return this.state.errorData?.notice;
  }

  get error() {
    return this.props.error || this.state.error;
  }

  get hasError() {
    return this.state.hasError;
  }
}

export default BaseErrorBoundary;
